import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import {
  Column,
  ColumnChooserService,
  DetailRowService,
  ExcelExportProperties,
  ExcelExportService,
  GridComponent as SyncGridComponent,
  GridModel,
  GroupService,
  GroupSettingsModel,
  SelectionSettingsModel,
  ToolbarItems,
  ToolbarService,
  ReorderService,
  ResizeService
} from '@syncfusion/ej2-angular-grids';
import { ClickEventArgs } from '@syncfusion/ej2-angular-navigations';
import { MouseEventArgs } from '@syncfusion/ej2-base';
import { Predicate } from '@syncfusion/ej2-data';
import {
  DataStateChangeEventArgs,
  SortSettingsModel
} from '@syncfusion/ej2-grids';
import { QueryCellInfoEventArgs } from '@syncfusion/ej2-angular-grids';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  Subject,
  Subscription
} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';

import { Constants } from './../../constants';
import { LanaColumnModel } from './../../models/column';
import { FilterSettings } from './../../models/filter-settings';
import { AuthService } from './../../services/auth.service';
import { CompanyService } from './../../services/company.service';
import { Tooltip } from '@syncfusion/ej2-popups';
import * as moment from 'moment-timezone';
import { AdministrationService } from '@app/services/administration.service';

@Component({
  selector: 'lana-dynamic-grid',
  templateUrl: './dynamic-grid.component.html',
  styleUrls: ['./dynamic-grid.component.scss'],
  providers: [
    GroupService,
    ToolbarService,
    ExcelExportService,
    ColumnChooserService,
    DetailRowService,
    ReorderService,
    ResizeService
  ]
})
export class DynamicGridComponent implements OnInit, OnDestroy {
  administrationRrepotsPermission: any;
  constructor(
    private formBuilder: UntypedFormBuilder,
    private authService: AuthService,
    private companyService: CompanyService,
    private AdministrationService: AdministrationService
  ) {}

  @Input() columns: LanaColumnModel<any>[];
  @Input() dynamicColumns: Subject<any>;
  @Input() childColumns: any;
  @Input() dataService: Subject<DataStateChangeEventArgs> & {
    startTime;
    endTime;
    execute: (
      state: DataStateChangeEventArgs & { includeSubCompany: boolean }
    ) => void;
  } & {
    getExportAssets: (
      state: DataStateChangeEventArgs,
      includeSubCompany: boolean
    ) => void;
  } & { exportData$: any } & {
    childData: any;
  } & {
    getExport: (
      state: DataStateChangeEventArgs,
      includeSubCompany: boolean
    ) => any;
  };
  @Input() filterSettings: FilterSettings;
  @Input() sortSettings: SortSettingsModel;
  @Input() link: string;
  @Input() defaultState: any;
  @Input() isSelectable = false;
  @Input() isModal = false;
  @Input() includeSubCompanyText: string;
  @Input() includeSubCompany = false;
  @Input() displayEntityBreadcrumb = false;
  @Input() allowGrouping = false;
  @Input() allowExcelExport = false;
  @Input() excelExportObservable = false;
  @Input() exportVisible: any = {};
  @Input() exportFileName: string = 'ExcelExport';
  @Input() groupSettings: GroupSettingsModel = {};
  @Input() customClass: string = 'customClass';
  @Input() selectionOptions: SelectionSettingsModel;
  @Input() pageNo$: Observable<any>;

  @Output() rowSelected = new EventEmitter<any>();
  @Output() locationSelected = new EventEmitter<any>();
  @Output() statusSelected = new EventEmitter<any>();
  @Output() statusSelectedCancel = new EventEmitter<any>();

  @ViewChild('grid') grid: SyncGridComponent;
  allowResizing: boolean;
  private destroy: Subject<void>;

  readonly companySummaryField = 'companySummary';
  readonly companySummaryNameField = `${this.companySummaryField}.name`;
  readonly loading = new BehaviorSubject(false);

  toolbar: ToolbarItems[];
  data: Observable<DataStateChangeEventArgs>;
  formGroup: UntypedFormGroup;
  pageOptions: object;
  placeholder = '';
  state: DataStateChangeEventArgs & { includeSubCompany: boolean } = {
    skip: 0,
    take: Constants.DefaultPageSize,
    includeSubCompany: false
  };
  includeSubCompanyFormControl: UntypedFormControl;
  dataExport;
  companyId: number;
  showSubCompanyCheckbox = false;
  public childData: any;
  public childGrid: GridModel;
  public customAttributes: Object;
  public tz;
  fullData$: Observable<any>;

  dataStateChange(
    state: DataStateChangeEventArgs,
    includeSubCompanyFormControl: UntypedFormControl
  ): void {
    const includeSubCompany = includeSubCompanyFormControl
      ? includeSubCompanyFormControl.value
      : true;
    const newState = { ...state, includeSubCompany };

    this.grid.showSpinner();
    this.dataService.execute(newState);
    this.state = newState;
  }

  private handleFilterChange(value: string, includeSubCompany: boolean): void {
    // const where = value
    //   ? [
    //       {
    //         isComplex: true,
    //         ignoreAccent: false,
    //         condition: 'or',
    //         predicates: this.filterSettings.columns.map(column => ({
    //           ...column,
    //           value,
    //         })),
    //       },
    //     ]
    //   : [];

    const where: Predicate[] = value
      ? ([
          {
            isComplex: true,
            ignoreAccent: false,
            condition: 'or',
            predicates: this.filterSettings.columns.map((column) => ({
              ...column,
              value
            }))
          }
        ] as unknown as Predicate[])
      : [];

    this.grid.showSpinner();
    this.dataService.execute({ ...this.state, includeSubCompany, where });

    this.state.includeSubCompany = includeSubCompany;
    // if (this.excelExportObservable && includeSubCompany) {
    //   this.dataService.getExportAssets(this.state, includeSubCompany);
    // }
  }

  headerCellInfo(args: any): void {
    args.node.getElementsByClassName('e-checkbox-wrapper')[0] &&
      args.node.getElementsByClassName('e-checkbox-wrapper')[0].remove();
  }

  locationModal(data) {
    this.locationSelected.emit(data);
  }

  statusModal(data, type) {
    if (type === 'resend') {
      this.statusSelected.emit(data);
    } else {
      this.statusSelectedCancel.emit(data);
    }
  }

  rowSelecting({ data }: { data: any }): void {
    if (this.grid.getSelectedRecords().length) {
      this.grid.clearSelection();
    }
    this.rowSelected.emit(data);
  }

  load(args): void {
    if (this.grid?.element) {
      this.grid.element.addEventListener('mousedown', (e: MouseEventArgs) => {
        if (
          (e.target as HTMLElement).classList.contains('e-rowcell') &&
          this.isModal
        ) {
          this.grid.clearSelection();
        }
      });
    }
    if (this.childColumns !== undefined) {
      this.grid.childGrid.dataSource = this.dataService.childData;
    }

    if (this.pageNo$) {
      this.pageNo$
        .pipe(
          tap((pageNo) => {
            this.grid.pageSettings.currentPage = pageNo;
          })
        )
        .subscribe();
    }
  }

  toolbarClick(args: ClickEventArgs): void {
    if (args.item.id.includes('excelexport')) {
      this.loading.next(true);
      this.dataExport = this.dataService
        .getExport(this.state, this.state.includeSubCompany)
        .pipe(
          tap((data: any) => {
            if (data !== null && data?.result.length > 0) {
              const arr = Object.keys(this.exportVisible);
              for (let i = 0; i < arr.length; i++) {
                const type = arr[i];
                for (let j = 0; j < this.exportVisible[type].length; j++) {
                  const col = this.exportVisible[type][j];
                  (this.grid.columns[col] as Column).visible =
                    type === 'true' ? true : false;
                }
              }
              // csvExport is faster than excel with similar output
              if (args.item.id.includes('excelexport')) {
                const excelExportProperties: ExcelExportProperties = {
                  fileName: `${this.exportFileName}.csv`,
                  dataSource: data.result
                };
                this.grid.csvExport(excelExportProperties);
              }
            } else {
              this.grid.hideSpinner();
              this.loading.next(false);
            }
          })
        )
        .subscribe();
    }
  }

  excelExportComplete(): void {
    const arr = Object.keys(this.exportVisible);
    for (let i = 0; i < arr.length; i++) {
      const type = arr[i];
      for (let j = 0; j < this.exportVisible[type].length; j++) {
        const col = this.exportVisible[type][j];
        (this.grid.columns[col] as Column).visible =
          type === 'true' ? false : true;
      }
    }
    this.dataExport.unsubscribe();
    this.loading.next(false);
  }

  checkVisibility(data): boolean {
    return data?.result?.[0]?.showCompanyCol
      ? data.result[0].showCompanyCol
      : false;
  }

  displayTimeTooltip(args: QueryCellInfoEventArgs, prop: string): void {
    const lastKnownBatteryVoltage = args?.data[prop];
    const timestampProp = `${prop}Timestamp`;
    const timestamp = args?.data[timestampProp];

    if (lastKnownBatteryVoltage || lastKnownBatteryVoltage === 0) {
      const time = moment(timestamp)
        ?.tz(this.tz)
        ?.format('M/D/YYYY h:mm:ss A z');
      const tooltip: Tooltip = new Tooltip(
        {
          content: `Last Received: ${time}`
        },
        args.cell as HTMLTableCellElement
      );
    }
  }

  tooltip(args: QueryCellInfoEventArgs) {
    const field = args?.column?.field;
    const fieldLowerCase = field ? args.column.field.toLowerCase() : field;

    if (
      fieldLowerCase &&
      (fieldLowerCase.includes('assetbatteryvoltage') ||
        fieldLowerCase.includes('trackerbatteryvoltage'))
    ) {
      this.displayTimeTooltip(args, field);
    }
  }

  ngOnInit(): void {
    this.state = this.defaultState
      ? {
          ...this.state,
          ...this.defaultState,
          includeSubCompany: this.includeSubCompany
        }
      : { ...this.state, includeSubCompany: this.includeSubCompany };
    this.pageOptions = { pageSize: Constants.DefaultPageSize, pageCount: 5 };
    this.AdministrationService.AdministrationRrepots.subscribe((res) => {
      this.administrationRrepotsPermission = res;
    });
    let toolbar: any[] = ['ColumnChooser'];
    let i = 1;
    this.administrationRrepotsPermission.forEach((element) => {
      if (element.value == 'excelExport' && element.isActive == true) {
        toolbar[i] = 'ExcelExport';
      } else if (element.value == 'pdfExport' && element.isActive == true) {
        toolbar[i] = 'PdfExport';
      }
      if (element.value == 'columnSelector' && element.isActive == true) {
        this.selectionOptions = {
          allowColumnSelection: true,
          type: 'Multiple'
        };
      }
      if (element.value == 'columnResizing' && element.isActive == true) {
        this.allowResizing = true;
      }
      if (element.value == 'columnGrouping' && element.isActive == true) {
        this.allowGrouping = true;
      }
      i = i + 1;
    });
    this.toolbar = toolbar;
    this.formGroup = this.formBuilder.group({
      startDateTime: this.formBuilder.control(this.dataService.startTime),
      endDateTime: this.formBuilder.control(this.dataService.endTime)
    });

    this.data = this.dataService;
    this.dataService.execute(this.state);
    this.authService.currentUser$
      .pipe(
        tap((data) => (this.tz = data['ianaTimeZone'] ?? 'America/New_York'))
      )
      .subscribe();

    const dynamicColumns$ = this.dynamicColumns ? this.dynamicColumns : of([]);
    this.fullData$ = combineLatest(this.data, dynamicColumns$).pipe(
      tap(([_, dynamicColumns]) => {
        if (dynamicColumns && dynamicColumns.length > 0) {
          const columns = dynamicColumns.map((column) => {
            delete column.key;
            return { ...column, autoFit: true, visible: false };
          });
          this.columns = [...this.columns, ...columns];

          this.columns = this.columns.map((column) => {
            return { ...column, dynamic: true };
          });
        }
      }),
      map(([data, dynamicColumns]) => {
        return { data, dynamicColumns };
      })
    );

    this.authService.companyId$
      .pipe(
        switchMap((companyId) =>
          this.companyService.getCompanyHierarchy().pipe(
            map((companies) =>
              companies.map((company) => {
                if (company.id === companyId) {
                  this.showSubCompanyCheckbox = company.hasChild;
                }
              })
            )
          )
        )
      )
      .subscribe();

    if (this.childColumns !== undefined) {
      this.childGrid = {
        queryString: 'tripId',
        columns: this.childColumns,
        enableHover: false
      };
    }

    this.placeholder = this.filterSettings.columns.reduce(
      (working, next, index, values) => {
        return values.length === 1
          ? `${working}${next.headerText}`
          : index === values.length - 1
          ? `${working}or ${next.headerText}`
          : `${working}${next.headerText}${
              index === values.length - 2 ? ' ' : ', '
            }`;
      },
      'Search '
    );

    // if (this.filterSettings.columns.length > 0) {

    this.destroy = new Subject();
    this.formGroup = this.formBuilder.group({
      search: this.formBuilder.control('')
    });

    this.formGroup
      .get('search')
      .valueChanges.pipe(
        takeUntil(this.destroy),
        map((value) => ((value as string) || '').trim().toLowerCase()),
        distinctUntilChanged(),
        debounceTime(500),
        tap((value) =>
          this.handleFilterChange(
            value,
            this.includeSubCompanyFormControl
              ? this.includeSubCompanyFormControl.value
              : true
          )
        )
      )
      .subscribe();

    if (this.includeSubCompanyText) {
      this.includeSubCompanyFormControl = this.formBuilder.control(
        this.includeSubCompany
      );

      if (typeof this.formGroup !== 'undefined') {
        this.includeSubCompanyFormControl.valueChanges
          .pipe(
            takeUntil(this.destroy),
            tap((value) =>
              this.handleFilterChange(this.formGroup.get('search').value, value)
            )
          )
          .subscribe();
      } else {
        this.includeSubCompanyFormControl.valueChanges
          .pipe(tap((value) => this.handleFilterChange(null, value)))
          .subscribe();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.destroy) {
      this.destroy.next();
    }
  }
}
